import styled, { css, keyframes } from 'styled-components';

/**
 * Enhanced Button components with modern glass-morphism effect
 * Usage:
 * <Button>Default Button</Button>
 * <PrimaryButton>Primary Button</PrimaryButton>
 * <SecondaryButton>Secondary Button</SecondaryButton>
 * <IconButton><SomeIcon /></IconButton>
 */

// Pulse animation for hover state
const pulseBorder = keyframes`
  0% { box-shadow: 0 0 0 0 rgba(99, 102, 241, 0.7); }
  70% { box-shadow: 0 0 0 6px rgba(99, 102, 241, 0); }
  100% { box-shadow: 0 0 0 0 rgba(99, 102, 241, 0); }
`;

// Base button with shared styles
export const Button = styled.button`
  background: rgba(15, 23, 42, 0.6);
  color: rgba(255, 255, 255, 0.9);
  border: 1px solid rgba(148, 163, 184, 0.3);
  border-radius: 8px;
  padding: 0.75rem 1.5rem;
  font-size: 0.95rem;
  font-weight: 500;
  letter-spacing: 0.5px;
  cursor: pointer;
  transition: all 0.25s ease;
  backdrop-filter: blur(8px);
  position: relative;
  overflow: hidden;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  gap: 0.5rem;
  
  /* Fancy gradient effect inside */
  &:before {
    content: '';
    position: absolute;
    top: 0;
    left: 0;
    right: 0;
    bottom: 0;
    background: linear-gradient(
      135deg,
      rgba(255, 255, 255, 0.1) 0%,
      rgba(99, 102, 241, 0.1) 50%,
      rgba(15, 23, 42, 0.1) 100%
    );
    z-index: -1;
  }
  
  /* Shine-like effect */
  &:after {
    content: '';
    position: absolute;
    width: 100%;
    height: 100%;
    top: -100%;
    left: -100%;
    background: linear-gradient(
      45deg,
      transparent 0%,
      rgba(255, 255, 255, 0.1) 45%,
      rgba(255, 255, 255, 0.3) 50%,
      rgba(255, 255, 255, 0.1) 55%,
      transparent 100%
    );
    transition: all 0.6s ease;
    z-index: 1;
  }
  
  &:hover {
    transform: translateY(-2px);
    background: rgba(15, 23, 42, 0.75);
    box-shadow: 
      0 7px 20px -5px rgba(31, 38, 135, 0.3),
      0 0 0 2px rgba(99, 102, 241, 0.2);
    text-shadow: 0 0 8px rgba(99, 102, 241, 0.5);
    
    &:after {
      top: 100%;
      left: 100%;
    }
  }
  
  &:focus {
    outline: none;
    animation: ${pulseBorder} 1.5s infinite;
  }
  
  &:active {
    transform: translateY(1px);
    box-shadow: 0 5px 15px -5px rgba(31, 38, 135, 0.4);
  }
  
  /* Disabled state */
  &:disabled {
    opacity: 0.6;
    cursor: not-allowed;
    background: rgba(15, 23, 42, 0.4);
    border: 1px solid rgba(148, 163, 184, 0.15);
    
    &:hover {
      transform: none;
      box-shadow: none;
      text-shadow: none;
    }
  }
  
  ${props => props.fullWidth && css`
    width: 100%;
  `}
  
  ${props => props.size === 'small' && css`
    padding: 0.4rem 0.8rem;
    font-size: 0.8rem;
  `}
  
  ${props => props.size === 'large' && css`
    padding: 0.9rem 1.8rem;
    font-size: 1rem;
  `}
`;

// Primary action button with more prominent styling
export const PrimaryButton = styled(Button)`
  background: rgba(79, 70, 229, 0.85);
  border: 1px solid rgba(99, 102, 241, 0.5);
  
  &:before {
    background: linear-gradient(
      135deg,
      rgba(129, 140, 248, 0.2) 0%,
      rgba(99, 102, 241, 0.2) 50%,
      rgba(79, 70, 229, 0.1) 100%
    );
  }
  
  &:hover {
    background: rgba(79, 70, 229, 0.95);
    box-shadow: 
      0 8px 25px -5px rgba(79, 70, 229, 0.5),
      0 0 0 2px rgba(129, 140, 248, 0.3);
  }
`;

// Secondary/outline button option
export const SecondaryButton = styled(Button)`
  background: rgba(15, 23, 42, 0.3);
  border: 1.5px solid rgba(99, 102, 241, 0.6);
  box-shadow: inset 0 0 10px rgba(99, 102, 241, 0.1);
  
  &:hover {
    background: rgba(99, 102, 241, 0.15);
    border-color: rgba(99, 102, 241, 0.8);
  }
`;

// Icon button - for when you need a smaller button with just an icon
export const IconButton = styled(Button)`
  padding: 0.6rem;
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  width: 2.5rem;
  height: 2.5rem;
  
  svg {
    width: 1.25rem;
    height: 1.25rem;
    fill: currentColor;
  }
  
  &:hover svg {
    filter: drop-shadow(0 0 3px rgba(99, 102, 241, 0.6));
  }
`;

// For backward compatibility with existing code using different variants
const VariantButton = styled(Button)`
  ${props => {
    switch(props.variant) {
      case 'primary':
        return css`
          background: rgba(79, 70, 229, 0.85);
          border: 1px solid rgba(99, 102, 241, 0.5);
          
          &:before {
            background: linear-gradient(
              135deg,
              rgba(129, 140, 248, 0.2) 0%,
              rgba(99, 102, 241, 0.2) 50%,
              rgba(79, 70, 229, 0.1) 100%
            );
          }
          
          &:hover {
            background: rgba(79, 70, 229, 0.95);
            box-shadow: 
              0 8px 25px -5px rgba(79, 70, 229, 0.5),
              0 0 0 2px rgba(129, 140, 248, 0.3);
          }
        `;
      case 'secondary':
        return css`
          background: rgba(15, 23, 42, 0.3);
          border: 1.5px solid rgba(99, 102, 241, 0.6);
          
          &:hover {
            background: rgba(99, 102, 241, 0.15);
            border-color: rgba(99, 102, 241, 0.8);
          }
        `;
      case 'accent':
        return css`
          background: rgba(14, 165, 233, 0.85);
          border: 1px solid rgba(56, 189, 248, 0.5);
          
          &:hover {
            background: rgba(14, 165, 233, 0.95);
            box-shadow: 
              0 8px 25px -5px rgba(14, 165, 233, 0.5),
              0 0 0 2px rgba(56, 189, 248, 0.3);
          }
        `;
      case 'warning':
        return css`
          background: rgba(234, 179, 8, 0.85);
          border: 1px solid rgba(250, 204, 21, 0.5);
          
          &:hover {
            background: rgba(234, 179, 8, 0.95);
            box-shadow: 
              0 8px 25px -5px rgba(234, 179, 8, 0.5),
              0 0 0 2px rgba(250, 204, 21, 0.3);
          }
        `;
      case 'danger':
        return css`
          background: rgba(239, 68, 68, 0.85);
          border: 1px solid rgba(248, 113, 113, 0.5);
          
          &:hover {
            background: rgba(239, 68, 68, 0.95);
            box-shadow: 
              0 8px 25px -5px rgba(239, 68, 68, 0.5),
              0 0 0 2px rgba(248, 113, 113, 0.3);
          }
        `;
      case 'outline':
        return css`
          background: rgba(15, 23, 42, 0.3);
          border: 1.5px solid rgba(99, 102, 241, 0.6);
          box-shadow: inset 0 0 10px rgba(99, 102, 241, 0.1);
          
          &:hover {
            background: rgba(99, 102, 241, 0.15);
            border-color: rgba(99, 102, 241, 0.8);
          }
        `;
      default:
        return '';
    }
  }}
`;

// Bundle all button components together
const ButtonComponents = {
  Button,
  PrimaryButton,
  SecondaryButton,
  IconButton,
  VariantButton
};

export default ButtonComponents;
